import { useEffect, useState } from 'react';
import { Banner, Button, Checkbox, Dialog, Select, Skeleton, useToast } from '../../../../ds/components';
import { ApiError, errorMessage } from '../../../../lib/api';
import { useDeactivateUser, useDeactivationPreview, type UserTableRow } from './api';
import styles from './UsersPage.module.css';

interface DeactivateDialogProps {
  row: UserTableRow | null;
  onClose: () => void;
}

/**
 * Admin 06 „Nutzer deaktivieren": the handover figures of `GET /users/:id/deactivation`, the successor (default
 * from the server), „Termine automatisch umbuchen" and „Deaktivieren und übergeben" → `POST /users/:id/deactivate`
 * (one transaction on the server: contacts, deals, tasks, appointments … go to the successor).
 */
export function DeactivateDialog({ row, onClose }: DeactivateDialogProps) {
  const { toast } = useToast();
  const preview = useDeactivationPreview(row?.id ?? null);
  const deactivate = useDeactivateUser();
  const [successorId, setSuccessorId] = useState('');
  const [rebook, setRebook] = useState(true);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const data = preview.data;

  useEffect(() => {
    if (!data) return;
    setSuccessorId(data.defaultSuccessorId ?? '');
    setRebook(data.rebookAppointmentsDefault);
    setFieldError(null);
    setBanner(null);
  }, [data]);

  if (!row) return null;
  const current = row;

  async function submit() {
    setFieldError(null);
    setBanner(null);
    try {
      await deactivate.mutateAsync({
        id: current.id,
        successorId: successorId === '' ? null : successorId,
        rebookAppointments: rebook,
      });
      const successor = data?.successors.find((user) => user.id === successorId);
      toast({
        message: successor
          ? `${data?.user.shortName ?? current.name} ist deaktiviert. Offene Arbeit liegt jetzt bei ${successor.shortName}.`
          : `${current.name} ist deaktiviert.`,
      });
      onClose();
    } catch (error) {
      if (error instanceof ApiError && (error.fields['successorId'] || error.code === 'SUCCESSOR_REQUIRED')) {
        setFieldError(error.fields['successorId'] ?? error.message);
      } else setBanner(errorMessage(error));
    }
  }

  const figures = data
    ? [
        { label: 'Kontakte als Besitzer', value: String(data.counts.ownedContacts) },
        { label: 'Aktive Deals', value: String(data.counts.activeDeals) },
        { label: 'Offene Tasks', value: String(data.counts.openTasks) },
        { label: 'Termine ab heute', value: data.appointmentsLabel },
        ...(data.counts.assignedFinancings > 0
          ? [{ label: 'Finanzierungen', value: String(data.counts.assignedFinancings) }]
          : []),
        { label: 'Provision offen', value: data.counts.openCommission.label },
      ]
    : [];

  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow="Nutzer deaktivieren"
      title={data?.title ?? `${current.name} deaktivieren?`}
      subtitle="Der Zugang endet sofort. Name und Verlauf bleiben sichtbar, offene Arbeit geht an den Nachfolger."
      width={580}
      overlayTop={60}
      footerNote="Reaktivieren jederzeit möglich"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button loading={deactivate.isPending} disabled={!data} onClick={() => void submit()}>
            Deaktivieren und übergeben
          </Button>
        </>
      }
    >
      {preview.isError ? <Banner tone="danger">{errorMessage(preview.error)}</Banner> : null}
      {preview.isPending ? (
        <div className={styles.figures} aria-busy="true">
          {[0, 1, 2, 3, 4].map((index) => (
            <div key={index} className={styles.figure}>
              <Skeleton width="60%" />
            </div>
          ))}
        </div>
      ) : null}
      {data ? (
        <>
          <dl className={styles.figures} style={{ margin: 0 }}>
            {figures.map((figure) => (
              <div key={figure.label} className={styles.figure}>
                <dt className={styles.figureLabel}>{figure.label}</dt>
                <dd className={styles.figureValue} style={{ margin: 0 }}>
                  {figure.value}
                </dd>
              </div>
            ))}
          </dl>
          <Select
            label="Nachfolger"
            required
            value={successorId}
            placeholder="Nachfolger wählen"
            options={data.successors.map((user) => ({ value: user.id, label: user.shortName }))}
            hint="Bekommt Kontakte, Deals und Tasks. Termine werden ihm angeboten."
            error={fieldError ?? undefined}
            onChange={(event) => setSuccessorId(event.target.value)}
          />
          <Checkbox
            label="Termine automatisch umbuchen"
            description="Kunden erhalten eine E-Mail mit dem neuen Berater."
            checked={rebook}
            onChange={(event) => setRebook(event.target.checked)}
          />
          {banner ? <Banner tone="danger">{banner}</Banner> : null}
        </>
      ) : null}
    </Dialog>
  );
}
